import styled from '@emotion/styled';
import Flex from '@react-css/flex';
import classNames from 'classnames';
import { forwardRef } from 'react';
import type { FieldError } from 'react-hook-form';

import Body from 'storybookConfig/stories/molecules/Body';
import IconButton from 'storybookConfig/stories/molecules/Button/IconButton';
import Input from 'storybookConfig/stories/molecules/Input';
import TextAreaInput from 'storybookConfig/stories/molecules/Input/TextAreaInput';
import Label, { type LabelProps } from 'storybookConfig/stories/molecules/Label';
import type { IconName } from 'storybookConfig/mixins/icons';

/** An icon button beside a field, e.g. copying its value or showing a password. */
export interface FieldAction {
  name: string;
  /** The action, read with the field's label: "Copy" → "Copy Host". */
  label?: string;
  icon: IconName;
  event: (eventData: any) => void;
  eventData: any;
}

/**
 * The DS `Label`. `size` and `labelSize` are kept for old call sites and ignored: the app has one
 * field size.
 * @deprecated Use `Label` from `storybookConfig/stories/molecules/Label`.
 */
export const FormFieldLabel = ({
  size,
  labelSize,
  description,
  ...labelProps
}: LabelProps & { size?: string; labelSize?: string; description?: string }) => (
  // eslint-disable-next-line jsx-a11y/label-has-associated-control -- the caller passes htmlFor
  <Label {...labelProps} />
);

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

interface FormFieldProps {
  error?: { message: string } | FieldError;
  errorAlignRight?: boolean;
  label?: string;
  subLabel?: string;
  id?: string;
  type?: string;
  size?: string;
  className?: string;
  isTextarea?: boolean;
  description?: string;
  ungrouped?: boolean;
  isHorizontal?: boolean;
  labelSize?: string;
  appendInput?: FieldAction[];
  // The rest of the props should be React.HTMLProps<HTMLInputElement>
  // But there's a conflict with the `size` prop
  [key: string]: any;
}

/**
 * A labelled field drawn with the DS: the label above a 44px `Input` (or a `TextAreaInput`) with
 * any `appendInput` actions beside it, the description, then the error or the sub-label below.
 * `size`, `labelSize` and `isHorizontal` are kept for old call sites and ignored.
 * @deprecated Use `FormField` from `storybookConfig/stories/cells/FormField` with an `Input`.
 */
const FormField = forwardRef<HTMLInputElement | HTMLTextAreaElement, FormFieldProps>(
  (props, ref) => {
    const {
      error,
      errorAlignRight,
      label,
      subLabel,
      id,
      type,
      size,
      className,
      isTextarea,
      description,
      ungrouped,
      isHorizontal,
      labelSize,
      appendInput,
      ...inputProps
    } = props;

    // forwardRef's props keep only the index signature, so the declared type is restated here.
    const actions = appendInput as FieldAction[] | undefined;
    const errorId = id && error ? `${id}-error` : undefined;
    const fieldProps = {
      id,
      isFullWidth: true,
      'aria-invalid': !!error,
      'aria-describedby': errorId,
      ...inputProps,
    };

    const field = (
      <Field className={classNames({ [className ?? '']: ungrouped })}>
        {label && id && (
          <Label htmlFor={id} hasError={!!error}>
            {label}
          </Label>
        )}

        {description && (
          <Body variant="Body/Body Small" color="bodyTextSecondary">
            {description}
          </Body>
        )}

        {isTextarea ? (
          <TextAreaInput
            as="textarea"
            ref={ref as React.Ref<HTMLInputElement>}
            {...(fieldProps as React.ComponentProps<typeof TextAreaInput>)}
          />
        ) : (
          <Flex alignItemsCenter gap="8px">
            <Input type={type} ref={ref as React.Ref<HTMLInputElement>} {...fieldProps} />
            {actions?.map((input, index) => (
              <IconButton
                // eslint-disable-next-line react/no-array-index-key
                key={`${input.name}-${index}`}
                type="button"
                name={input.icon}
                aria-label={input.label ? `${input.label} ${label ?? ''}`.trim() : input.name}
                onClick={() => input.event(input.eventData)}
              />
            ))}
          </Flex>
        )}

        {error && (
          <Body
            id={errorId}
            variant="Body/Body Small"
            color="errorBase"
            align={errorAlignRight ? 'right' : undefined}
          >
            {error.message}
          </Body>
        )}

        {subLabel && !error && (
          <Body variant="Body/Body Small" color="bodyTextSecondary">
            {subLabel}
          </Body>
        )}
      </Field>
    );

    return ungrouped ? (
      field
    ) : (
      <div role="group" className={classNames('form-group', className)}>
        {field}
      </div>
    );
  }
);

export default FormField;
